import { useMemo } from "react";
import { SettingsService } from "@/services/SettingsService";
import { useQuery } from "@tanstack/react-query";
import type { FeatureFlags } from "@/api/settingsApi";

interface UseFeatureFlagsQueryOptions {
  enabled?: boolean;
  staleTime?: number;
}

/**
 * Reads runtime feature flags from GET /settings/features (authenticated; any role — unlike the
 * superadmin-only full settings). Used to gate UI such as the R24a "Start Slot Here" button for
 * the admin/PM slot-create flow. Defaults the flag OFF until/if the query resolves.
 */
export function useFeatureFlagsQuery(options?: UseFeatureFlagsQueryOptions) {
  const { enabled = true, staleTime = 5 * 60 * 1000 } = options || {}; // 5 minutes

  const settingsService = useMemo(() => new SettingsService(), []);

  return useQuery<FeatureFlags>({
    queryKey: ["feature-flags"],
    queryFn: () => settingsService.getFeatureFlags(),
    enabled,
    staleTime,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}
